import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Alert,
  Badge,
  Button,
  ButtonGroup,
  Card,
  Col,
  Container,
  Form,
  Image,
  Row,
  Spinner,
  Table,
} from 'react-bootstrap';
import api from '../api';

const POLL_MS = 2000;

function safeJsonParse(value) {
  if (!value) return null;

  if (typeof value !== 'string') {
    return value;
  }

  try {
    return JSON.parse(value);
  } catch {
    return null;
  }
}

function pickImageUrlFromValue(value) {
  if (!value) return null;

  if (typeof value === 'string') {
    if (value.startsWith('http://') || value.startsWith('https://')) {
      return value;
    }

    const parsed = safeJsonParse(value);
    if (parsed) return pickImageUrlFromValue(parsed);

    return null;
  }

  if (Array.isArray(value)) {
    for (const item of value) {
      const found = pickImageUrlFromValue(item);
      if (found) return found;
    }

    return null;
  }

  if (typeof value === 'object') {
    const direct =
      value.url ||
      value.src ||
      value.image ||
      value.image_url ||
      value.imageUrl ||
      value.thumbnail ||
      value.thumbnail_url ||
      value.thumbnailUrl ||
      value.main_image ||
      value.mainImage;

    if (direct) {
      const found = pickImageUrlFromValue(direct);
      if (found) return found;
    }

    if (value.shopify) {
      const found = pickImageUrlFromValue(value.shopify);
      if (found) return found;
    }

    if (value.ebay) {
      const found = pickImageUrlFromValue(value.ebay);
      if (found) return found;
    }

    if (value.walmart) {
      const found = pickImageUrlFromValue(value.walmart);
      if (found) return found;
    }
  }

  return null;
}

function unwrapProductResponse(data) {
  return (
    data?.product ||
    data?.item ||
    data?.data ||
    data?.result ||
    data ||
    null
  );
}

function getProductImageUrl(product) {
  if (!product) return null;

  return (
    pickImageUrlFromValue(product.product_images) ||
    pickImageUrlFromValue(product.images) ||
    pickImageUrlFromValue(product.image) ||
    pickImageUrlFromValue(product.image_url) ||
    pickImageUrlFromValue(product.imageUrl) ||
    pickImageUrlFromValue(product.thumbnail) ||
    pickImageUrlFromValue(product.thumbnail_url) ||
    pickImageUrlFromValue(product.thumbnailUrl) ||
    pickImageUrlFromValue(product.shopify_images) ||
    pickImageUrlFromValue(product.ebay_images) ||
    pickImageUrlFromValue(product.walmart_images) ||
    pickImageUrlFromValue(product.shopify_image) ||
    pickImageUrlFromValue(product.ebay_image) ||
    pickImageUrlFromValue(product.walmart_image) ||
    null
  );
}

function getItemImageUrl(item) {
  return (
    pickImageUrlFromValue(item?.product_images) ||
    pickImageUrlFromValue(item?.images) ||
    pickImageUrlFromValue(item?.image_url) ||
    pickImageUrlFromValue(item?.image) ||
    null
  );
}

function getStatusVariant(status) {
  if (status === 'matched') return 'success';
  if (status === 'needs_review') return 'warning';
  if (status === 'wrong_match') return 'danger';
  if (status === 'failed') return 'danger';
  if (status === 'removed') return 'secondary';
  return 'secondary';
}

function getStatusLabel(status) {
  const map = {
    matched: 'Matched',
    needs_review: 'Needs Review',
    wrong_match: 'Wrong Match',
    failed: 'Failed',
    removed: 'Removed',
    pending: 'Pending',
  };

  return map[status] || status || 'Unknown';
}

function formatDateTime(value) {
  if (!value) return '-';

  try {
    return new Date(value).toLocaleString();
  } catch {
    return String(value);
  }
}

function shortText(value, max = 80) {
  const text = String(value || '').trim();

  if (!text) return '-';

  if (text.length <= max) return text;

  return `${text.slice(0, max)}...`;
}

function renderSkuLink(sku, className = 'fw-bold text-primary') {
  if (!sku) {
    return <span className="fw-bold text-danger">Not matched</span>;
  }

  return (
    <Link
      to={`/edit/${encodeURIComponent(sku)}`}
      className={className}
      style={{ textDecoration: 'none' }}
      title={`Open ${sku}`}
    >
      {sku}
    </Link>
  );
}

export default function CardIntakeMonitor() {
  const [batches, setBatches] = useState([]);
  const [selectedBatchId, setSelectedBatchId] = useState('');
  const [batch, setBatch] = useState(null);
  const [summary, setSummary] = useState(null);
  const [items, setItems] = useState([]);

  const [filter, setFilter] = useState('issues');
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [loading, setLoading] = useState(false);
  const [actionLoadingId, setActionLoadingId] = useState(null);
  const [error, setError] = useState('');
  const [lastUpdated, setLastUpdated] = useState(null);

  const [productImagesBySku, setProductImagesBySku] = useState({});
  const productImagesBySkuRef = useRef({});

  const hasLoadedInitial = useRef(false);

  const loadBatches = async () => {
    const res = await api.get('/card-intake/batches', {
      params: {
        limit: 50,
      },
    });

    const nextBatches = res.data?.batches || [];
    setBatches(nextBatches);

    return nextBatches;
  };

  const pickDefaultBatch = async (batchList) => {
    if (selectedBatchId) return selectedBatchId;

    try {
      const latestRes = await api.get('/card-intake/batches/latest-running');
      const latestRunning = latestRes.data?.batch;

      if (latestRunning?.id) {
        setSelectedBatchId(String(latestRunning.id));
        return String(latestRunning.id);
      }
    } catch (err) {
      console.warn('[CardIntakeMonitor] Could not load latest running batch:', err.message);
    }

    const newest = batchList?.[0];

    if (newest?.id) {
      setSelectedBatchId(String(newest.id));
      return String(newest.id);
    }

    return '';
  };

  const loadBatchDetails = async (batchId) => {
    if (!batchId) {
      setBatch(null);
      setSummary(null);
      setItems([]);
      return;
    }

    const res = await api.get(`/card-intake/batches/${batchId}`);

    setBatch(res.data?.batch || null);
    setSummary(res.data?.summary || null);
    setItems(res.data?.items || []);
    setLastUpdated(new Date());
  };

  const loadAll = async ({ quiet = false } = {}) => {
    try {
      if (!quiet) setLoading(true);
      setError('');

      const nextBatches = await loadBatches();
      const batchId = await pickDefaultBatch(nextBatches);

      if (batchId) {
        await loadBatchDetails(batchId);
      }

      hasLoadedInitial.current = true;
    } catch (err) {
      console.error('[CardIntakeMonitor] Load error:', err);
      setError(err.response?.data?.error || err.message || 'Failed to load card intake monitor');
    } finally {
      if (!quiet) setLoading(false);
    }
  };

  const fetchProductImageForSku = async (sku) => {
    if (!sku) return null;

    try {
      const res = await api.get(`/products/${encodeURIComponent(sku)}`);
      const product = unwrapProductResponse(res.data);
      return getProductImageUrl(product);
    } catch (err) {
      console.warn(`[CardIntakeMonitor] Could not load image for SKU ${sku}:`, err.message);
      return null;
    }
  };

  useEffect(() => {
    loadAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!selectedBatchId || !hasLoadedInitial.current) return;

    loadBatchDetails(selectedBatchId).catch((err) => {
      console.error('[CardIntakeMonitor] Batch change load error:', err);
      setError(err.response?.data?.error || err.message || 'Failed to load selected batch');
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedBatchId]);

  useEffect(() => {
    if (!autoRefresh) return;

    const timer = setInterval(() => {
      loadAll({ quiet: true });
    }, POLL_MS);

    return () => clearInterval(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoRefresh, selectedBatchId]);

  useEffect(() => {
    const skus = Array.from(
      new Set(
        (items || [])
          .map((item) => item.matched_sku || item.product_sku)
          .filter(Boolean)
      )
    );

    const missingSkus = skus.filter((sku) => {
      return !Object.prototype.hasOwnProperty.call(productImagesBySkuRef.current, sku);
    });

    if (!missingSkus.length) return;

    let cancelled = false;

    async function loadMissingImages() {
      const nextImages = {};

      for (const sku of missingSkus) {
        productImagesBySkuRef.current[sku] = null;

        const imageUrl = await fetchProductImageForSku(sku);
        nextImages[sku] = imageUrl || null;
        productImagesBySkuRef.current[sku] = imageUrl || null;
      }

      if (!cancelled) {
        setProductImagesBySku((prev) => ({
          ...prev,
          ...nextImages,
        }));
      }
    }

    loadMissingImages();

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [items]);

  const filteredItems = useMemo(() => {
    const activeItems = items || [];

    if (filter === 'all') return activeItems;

    if (filter === 'issues') {
      return activeItems.filter((item) =>
        ['needs_review', 'wrong_match', 'failed'].includes(item.status)
      );
    }

    if (filter === 'matched') {
      return activeItems.filter((item) => item.status === 'matched');
    }

    if (filter === 'removed') {
      return activeItems.filter(
        (item) => item.status === 'removed' || Number(item.removed || 0) === 1
      );
    }

    if (filter === 'low_score') {
      return activeItems.filter(
        (item) => Number(item.match_score || 0) > 0 && Number(item.match_score || 0) < 100
      );
    }

    return activeItems;
  }, [items, filter]);

  const latestItem = useMemo(() => {
    const active = (items || []).filter((item) => Number(item.removed || 0) !== 1);
    return active.length ? active[active.length - 1] : null;
  }, [items]);

  const runAction = async (itemId, action) => {
    const labels = {
      approve: 'approve this scan',
      wrong: 'mark this as a wrong match and undo inventory if needed',
      remove: 'remove this scan and undo inventory if needed',
    };

    const needsConfirm = action === 'wrong' || action === 'remove';

    if (needsConfirm) {
      const ok = window.confirm(`Are you sure you want to ${labels[action]}?`);

      if (!ok) return;
    }

    try {
      setActionLoadingId(`${itemId}:${action}`);
      setError('');

      await api.post(`/card-intake/items/${itemId}/${action}`);

      if (selectedBatchId) {
        await loadBatchDetails(selectedBatchId);
      }
    } catch (err) {
      console.error(`[CardIntakeMonitor] ${action} error:`, err);
      setError(err.response?.data?.error || err.message || `Failed to ${action} scan`);
    } finally {
      setActionLoadingId(null);
    }
  };

  const renderImage = (item, large = false) => {
    const sku = item?.matched_sku || item?.product_sku;
    const imageUrl = getItemImageUrl(item) || productImagesBySku[sku] || null;

    if (!imageUrl) {
      return (
        <div
          className="d-flex align-items-center justify-content-center bg-light border rounded text-muted"
          style={{
            width: large ? 150 : 70,
            height: large ? 210 : 96,
            fontSize: large ? 14 : 11,
            textAlign: 'center',
            padding: 6,
          }}
        >
          No Image
        </div>
      );
    }

    return (
      <Image
        src={imageUrl}
        alt={item.product_name || item.matched_sku || 'Matched card'}
        rounded
        style={{
          width: large ? 150 : 70,
          height: large ? 210 : 96,
          objectFit: 'contain',
          background: '#fff',
          border: '1px solid #dee2e6',
        }}
      />
    );
  };

  const renderActionButton = (item, action, label, variant) => {
    const key = `${item.id}:${action}`;
    const disabled =
      actionLoadingId !== null ||
      item.status === 'removed' ||
      Number(item.removed || 0) === 1;

    return (
      <Button
        size="sm"
        variant={variant}
        disabled={disabled}
        onClick={() => runAction(item.id, action)}
      >
        {actionLoadingId === key ? (
          <>
            <Spinner animation="border" size="sm" className="me-1" />
            Working
          </>
        ) : (
          label
        )}
      </Button>
    );
  };

  return (
    <Container fluid className="py-4">
      <Row className="align-items-center mb-3">
        <Col>
          <h2 className="mb-1">Card Intake Monitor</h2>
          <div className="text-muted">
            Optional live control room. The scanner still runs automatically even if this page is closed.
          </div>
        </Col>

        <Col xs="auto">
          <ButtonGroup>
            <Button
              variant={autoRefresh ? 'success' : 'outline-secondary'}
              onClick={() => setAutoRefresh((value) => !value)}
            >
              {autoRefresh ? 'Live Refresh On' : 'Live Refresh Off'}
            </Button>

            <Button variant="primary" onClick={() => loadAll()} disabled={loading}>
              {loading ? (
                <>
                  <Spinner animation="border" size="sm" className="me-1" />
                  Loading
                </>
              ) : (
                'Refresh Now'
              )}
            </Button>
          </ButtonGroup>
        </Col>
      </Row>

      {error && (
        <Alert variant="danger" onClose={() => setError('')} dismissible>
          {error}
        </Alert>
      )}

      <Card className="mb-3">
        <Card.Body>
          <Row className="g-3 align-items-end">
            <Col md={4}>
              <Form.Label>Batch</Form.Label>
              <Form.Select
                value={selectedBatchId}
                onChange={(event) => setSelectedBatchId(event.target.value)}
              >
                <option value="">Select Batch</option>
                {batches.map((b) => (
                  <option key={b.id} value={b.id}>
                    {b.batch_code || `Batch ${b.id}`} - {b.status} - {formatDateTime(b.created_at)}
                  </option>
                ))}
              </Form.Select>
            </Col>

            <Col md={8}>
              {batch ? (
                <div className="d-flex flex-wrap gap-2">
                  <Badge bg={batch.status === 'running' ? 'success' : 'secondary'} className="p-2">
                    Status: {batch.status}
                  </Badge>
                  <Badge bg="dark" className="p-2">
                    Bin: {batch.bin_barcode || batch.bin_location || '-'}
                  </Badge>
                  <Badge bg="info" className="p-2">
                    Condition: {batch.condition_bucket || '-'}
                  </Badge>
                  <Badge bg="info" className="p-2">
                    Foil: {batch.foil_override || 'auto'}
                  </Badge>
                  <Badge bg="info" className="p-2">
                    Set: {batch.tcg_set || 'none'}
                  </Badge>
                  <Badge bg="secondary" className="p-2">
                    Last Updated: {lastUpdated ? lastUpdated.toLocaleTimeString() : '-'}
                  </Badge>
                </div>
              ) : (
                <div className="text-muted">No batch selected.</div>
              )}
            </Col>
          </Row>
        </Card.Body>
      </Card>

      <Row className="g-3 mb-3">
        <Col md={2}>
          <Card className="h-100">
            <Card.Body>
              <div className="text-muted small">Processed</div>
              <h3>{summary?.processed_count ?? batch?.processed_count ?? 0}</h3>
            </Card.Body>
          </Card>
        </Col>

        <Col md={2}>
          <Card className="h-100 border-success">
            <Card.Body>
              <div className="text-muted small">Matched</div>
              <h3>{summary?.matched_count ?? batch?.matched_count ?? 0}</h3>
            </Card.Body>
          </Card>
        </Col>

        <Col md={2}>
          <Card className="h-100 border-warning">
            <Card.Body>
              <div className="text-muted small">Review</div>
              <h3>{summary?.review_count ?? batch?.review_count ?? 0}</h3>
            </Card.Body>
          </Card>
        </Col>

        <Col md={2}>
          <Card className="h-100 border-danger">
            <Card.Body>
              <div className="text-muted small">Failed</div>
              <h3>{summary?.failed_count ?? batch?.failed_count ?? 0}</h3>
            </Card.Body>
          </Card>
        </Col>

        <Col md={2}>
          <Card className="h-100 border-primary">
            <Card.Body>
              <div className="text-muted small">Inventory Added</div>
              <h3>{summary?.inventory_applied_count ?? 0}</h3>
            </Card.Body>
          </Card>
        </Col>

        <Col md={2}>
          <Card className="h-100 border-secondary">
            <Card.Body>
              <div className="text-muted small">Removed</div>
              <h3>{summary?.removed_count ?? 0}</h3>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {latestItem && (
        <Card className="mb-3">
          <Card.Header className="d-flex align-items-center justify-content-between">
            <strong>Latest Scan</strong>
            <Badge bg={getStatusVariant(latestItem.status)}>
              {getStatusLabel(latestItem.status)}
            </Badge>
          </Card.Header>

          <Card.Body>
            <Row className="g-3">
              <Col xs="auto">{renderImage(latestItem, true)}</Col>

              <Col>
                <h4 className="mb-1">
                  {latestItem.detected_card_name || latestItem.product_name || 'Unknown Card'}
                </h4>

                <div className="text-muted mb-2">
                  {latestItem.detected_collector_number || '-'} · {latestItem.detected_set_name || '-'} ·{' '}
                  {latestItem.detected_foil_type || '-'} · {latestItem.selected_condition || '-'}
                </div>

                <div className="mb-2">
                  <strong>Matched SKU:</strong>{' '}
                  {renderSkuLink(latestItem.matched_sku, 'text-primary fw-bold')}
                </div>

                <div className="mb-2">
                  <strong>Product:</strong> {latestItem.product_name || '-'}
                </div>

                <div className="mb-2">
                  <strong>Qty:</strong>{' '}
                  {Number(latestItem.inventory_applied || 0) === 1 ? (
                    <span className="fw-bold text-success">
                      {latestItem.old_qty ?? '-'} → {latestItem.new_qty ?? '-'}
                    </span>
                  ) : (
                    <span className="text-muted">No inventory change</span>
                  )}
                </div>

                <div className="mb-2">
                  <strong>Score:</strong> {latestItem.match_score ?? 0}
                </div>

                {latestItem.error_reason && (
                  <Alert variant="warning" className="py-2 mb-2">
                    {latestItem.error_reason}
                  </Alert>
                )}

                <ButtonGroup>
                  {renderActionButton(latestItem, 'approve', 'Approve', 'success')}
                  {renderActionButton(latestItem, 'wrong', 'Wrong Match', 'danger')}
                  {renderActionButton(latestItem, 'remove', 'Remove Scan', 'outline-danger')}
                </ButtonGroup>
              </Col>
            </Row>
          </Card.Body>
        </Card>
      )}

      <Card>
        <Card.Header>
          <Row className="g-2 align-items-center">
            <Col>
              <strong>Live Feed</strong>
            </Col>

            <Col xs="auto">
              <ButtonGroup size="sm">
                <Button
                  variant={filter === 'issues' ? 'primary' : 'outline-primary'}
                  onClick={() => setFilter('issues')}
                >
                  Issues
                </Button>
                <Button
                  variant={filter === 'all' ? 'primary' : 'outline-primary'}
                  onClick={() => setFilter('all')}
                >
                  All
                </Button>
                <Button
                  variant={filter === 'matched' ? 'primary' : 'outline-primary'}
                  onClick={() => setFilter('matched')}
                >
                  Matched
                </Button>
                <Button
                  variant={filter === 'low_score' ? 'primary' : 'outline-primary'}
                  onClick={() => setFilter('low_score')}
                >
                  Low Score
                </Button>
                <Button
                  variant={filter === 'removed' ? 'primary' : 'outline-primary'}
                  onClick={() => setFilter('removed')}
                >
                  Removed
                </Button>
              </ButtonGroup>
            </Col>
          </Row>
        </Card.Header>

        <Card.Body className="p-0">
          {loading && !items.length ? (
            <div className="p-4 text-center">
              <Spinner animation="border" />
              <div className="mt-2 text-muted">Loading card monitor...</div>
            </div>
          ) : filteredItems.length === 0 ? (
            <div className="p-4 text-center text-muted">
              No cards found for this filter.
            </div>
          ) : (
            <div className="table-responsive">
              <Table hover className="mb-0 align-middle">
                <thead>
                  <tr>
                    <th style={{ width: 90 }}>Image</th>
                    <th>Status</th>
                    <th>Detected</th>
                    <th>Matched SKU</th>
                    <th>Qty</th>
                    <th>Score</th>
                    <th>Issue / Reason</th>
                    <th style={{ width: 250 }}>Actions</th>
                  </tr>
                </thead>

                <tbody>
                  {filteredItems
                    .slice()
                    .reverse()
                    .map((item) => {
                      const isRemoved = item.status === 'removed' || Number(item.removed || 0) === 1;
                      const inventoryApplied = Number(item.inventory_applied || 0) === 1;

                      return (
                        <tr key={item.id} className={isRemoved ? 'table-secondary' : ''}>
                          <td>{renderImage(item)}</td>

                          <td>
                            <Badge bg={getStatusVariant(item.status)}>
                              {getStatusLabel(item.status)}
                            </Badge>

                            {Number(item.approved || 0) === 1 && (
                              <div className="mt-1">
                                <Badge bg="success">Approved</Badge>
                              </div>
                            )}
                          </td>

                          <td>
                            <div className="fw-bold">
                              {item.detected_card_name || '-'}
                            </div>
                            <div className="small text-muted">
                              {item.detected_collector_number || '-'} · {item.detected_set_name || '-'}
                            </div>
                            <div className="small text-muted">
                              {item.detected_foil_type || '-'} · {item.selected_condition || '-'}
                            </div>
                            <div className="small text-muted">
                              File: {shortText(item.drive_file_name, 45)}
                            </div>
                          </td>

                          <td>
                            <div>
                              {renderSkuLink(item.matched_sku, 'fw-bold text-primary')}
                            </div>
                            <div className="small text-muted">
                              {shortText(item.product_name, 60)}
                            </div>
                          </td>

                          <td>
                            {inventoryApplied ? (
                              <span className="fw-bold text-success">
                                {item.old_qty ?? '-'} → {item.new_qty ?? '-'}
                              </span>
                            ) : (
                              <span className="text-muted">No change</span>
                            )}
                          </td>

                          <td>{item.match_score ?? 0}</td>

                          <td>
                            <div className={item.error_reason ? 'text-danger small' : 'text-muted small'}>
                              {shortText(item.error_reason || item.match_reason, 120)}
                            </div>
                          </td>

                          <td>
                            <ButtonGroup size="sm">
                              {renderActionButton(item, 'approve', 'Approve', 'success')}
                              {renderActionButton(item, 'wrong', 'Wrong', 'danger')}
                              {renderActionButton(item, 'remove', 'Remove', 'outline-danger')}
                            </ButtonGroup>
                          </td>
                        </tr>
                      );
                    })}
                </tbody>
              </Table>
            </div>
          )}
        </Card.Body>
      </Card>
    </Container>
  );
}